import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Edit2, Trash2, Zap, MessageSquare, Upload, X } from 'lucide-react';
import { featuresAPI, uploadAPI } from '../../utils/api';
import { toast } from 'sonner';

const AdminFeatures = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '',
    order: 0,
    is_active: true
  });
  const [uploading, setUploading] = useState(false);
  const [availableImages, setAvailableImages] = useState([]);
  const [showImagePicker, setShowImagePicker] = useState(false);

  useEffect(() => {
    fetchFeatures();
    fetchAvailableImages();
  }, []);

  const fetchFeatures = async () => {
    try {
      const response = await featuresAPI.getAllAdmin();
      setFeatures(response.data);
    } catch (error) {
      console.error('Error fetching features:', error);
      toast.error('Ошибка загрузки возможностей');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableImages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/files`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      const data = await response.json();
      const images = data.files.filter(file => 
        file.filename.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)
      );
      setAvailableImages(images);
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      icon: '',
      order: 0,
      is_active: true
    });
    setEditingFeature(null);
  };

  const openDialog = (feature = null) => {
    if (feature) {
      setFormData({
        title: feature.title,
        description: feature.description,
        icon: feature.icon,
        order: feature.order,
        is_active: feature.is_active
      });
      setEditingFeature(feature);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Пожалуйста, выберите изображение');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAPI.uploadFile(file);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      
      setFormData(prev => ({
        ...prev,
        icon: imageUrl
      }));
      
      fetchAvailableImages();
      toast.success('Иконка успешно загружена');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Ошибка загрузки иконки');
    } finally {
      setUploading(false);
    }
  };

  const selectExistingImage = (imageUrl) => {
    setFormData(prev => ({
      ...prev,
      icon: imageUrl
    }));
    setShowImagePicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      order: parseInt(formData.order)
    };

    try {
      if (editingFeature) {
        await featuresAPI.update(editingFeature.id, submitData);
        toast.success('Возможность успешно обновлена');
      } else {
        await featuresAPI.create(submitData);
        toast.success('Возможность успешно создана');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchFeatures();
    } catch (error) {
      console.error('Error saving feature:', error);
      toast.error('Ошибка сохранения возможности');
    }
  };

  const handleDelete = async (featureId) => {
    if (confirm('Вы уверены, что хотите удалить эту возможность?')) {
      try {
        await featuresAPI.delete(featureId);
        toast.success('Возможность успешно удалена');
        fetchFeatures();
      } catch (error) {
        console.error('Error deleting feature:', error);
        toast.error('Ошибка удаления возможности');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загружается...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Управление возможностями</h1>
          <p className="text-gray-400">Создание и редактирование возможностей бота</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => openDialog()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-feature-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Добавить возможность
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingFeature ? 'Редактировать возможность' : 'Создать новую возможность'}
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Заполните информацию о возможности бота
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-gray-300">Название возможности</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Перевод текста"
                    required
                    data-testid="feature-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="order" className="text-gray-300">Порядок отображения</Label>
                  <Input
                    id="order"
                    type="number"
                    value={formData.order}
                    onChange={(e) => handleInputChange('order', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="0"
                    data-testid="feature-order-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-gray-300">Описание</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white min-h-[120px]"
                  placeholder="Подробное описание возможности бота"
                  required
                  data-testid="feature-description-input"
                />
              </div>

              {/* Icon Selection */}
              <div className="space-y-2">
                <Label className="text-gray-300">Иконка (необязательно)</Label>
                
                {formData.icon && (
                  <div className="relative mb-4 inline-block">
                    <img 
                      src={formData.icon} 
                      alt="Icon preview" 
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, icon: '' }))}
                      className="absolute -top-1 -right-1 w-5 h-5 p-0 bg-red-600 hover:bg-red-700 rounded-full"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Input
                    value={formData.icon}
                    onChange={(e) => handleInputChange('icon', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white flex-1 min-w-0"
                    placeholder="URL иконки"
                    data-testid="feature-icon-input"
                  />
                  
                  <Button
                    type="button"
                    onClick={() => setShowImagePicker(!showImagePicker)}
                    variant="outline"
                    className="border-gray-600"
                  >
                    Выбрать из загруженных
                  </Button>
                  
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="icon-upload"
                    disabled={uploading}
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('icon-upload').click()}
                    disabled={uploading}
                    variant="outline"
                    className="border-gray-600"
                  >
                    <Upload className="mr-1 h-4 w-4" />
                    {uploading ? 'Загрузка...' : 'Загрузить'}
                  </Button>
                </div>

                {showImagePicker && availableImages.length > 0 && (
                  <div className="grid grid-cols-6 gap-2 p-3 bg-gray-700 rounded-lg max-h-40 overflow-y-auto">
                    {availableImages.map((img) => (
                      <div
                        key={img.filename}
                        className="cursor-pointer hover:opacity-75 relative"
                        onClick={() => selectExistingImage(`${process.env.REACT_APP_BACKEND_URL}${img.url}`)}
                      >
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`}
                          alt={img.filename}
                          className="w-full h-12 object-cover rounded"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                  data-testid="feature-active-switch"
                />
                <Label htmlFor="is_active" className="text-gray-300">Активная</Label>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-gray-600 text-gray-300"
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-feature-btn"
                >
                  {editingFeature ? 'Обновить' : 'Создать'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feature, index) => (
          <motion.div
            key={feature.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <Card className="bg-gray-800 border-gray-700 h-full group hover:border-gray-600 transition-colors">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      {feature.icon ? (
                        <img 
                          src={feature.icon} 
                          alt={feature.title}
                          className="w-8 h-8 object-cover rounded"
                        />
                      ) : (
                        <MessageSquare className="h-6 w-6 text-blue-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-white text-lg mb-1">{feature.title}</CardTitle>
                      <div className="flex items-center space-x-2">
                        <Badge className={feature.is_active ? 'bg-green-600' : 'bg-gray-600'}>
                          {feature.is_active ? 'Активна' : 'Неактивна'}
                        </Badge>
                        <Badge variant="secondary" className="bg-gray-700 text-gray-300">
                          Порядок: {feature.order}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1 ml-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDialog(feature)}
                      className="text-blue-400 hover:text-blue-300 p-1"
                      data-testid={`edit-feature-${feature.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(feature.id)}
                      className="text-red-400 hover:text-red-300 p-1"
                      data-testid={`delete-feature-${feature.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {features.length === 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="text-center py-12">
            <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">Возможности пока не добавлены</p>
            <Button 
              onClick={() => openDialog()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Добавить первую возможность
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminFeatures;